import { Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { ReportDataService } from '../report-data.service';
import { ReportService } from '../../_services/report.service';
import { AuthenticationService } from '../../_services/authentication.service';
import { ClientReportService } from '../../_services/client-report.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { buildClientReportFilter, getNativeReportCode } from '../client-report-filter.util';
import { ClientReportDownload } from '../client-report-download';
import {
  buildNativeBatchRequest, buildSendListRequest, linesForLearners, requiredLines, toRequestDate
} from '../client-report-send';
import { ReportFileFormat } from '../report-format-picker/report-format-picker.component';
import { MatDialog } from '@angular/material/dialog';
import { buildSendSummary, SendReportConfirmComponent } from '../send-report-confirm/send-report-confirm.component';
import { ReportFilterLookupsService } from '../report-filter-lookups.service';
import { ClientReportFilter } from '../../_models/reports/client-report-filter.model';
import { MilprReport } from '../../_models/reports/milpr-report.model';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { ALL_SESSION_REPORT_STATUS_IDS, SESSION_REPORT_STATUSES } from '../../_constants/session-report-statuses';
import { ReportGenerateData } from '../../_models/reports/report-generate-data.model';
import { ReportSentData } from '../../_models/reports/report-sent-data.model';
import { ReportSentDataRequest } from '../../_models/reports/report-sent-data-request.model';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, parseIsoDate, resolveDatePreset, toIsoDate } from '../../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../../shared/filter-ux/date-presets.component';

/** How many already-sent reports the panel shows; the History tab has the rest. */
export const ALREADY_SENT_PAGE_SIZE = 10;
/** How long the filters settle before the already-sent panel reloads. */
export const ALREADY_SENT_DEBOUNCE_MS = 400;
/** Rows the on-screen report shows; Download has the whole report. */
export const PREVIEW_ROW_LIMIT = 500;

/** What the on-screen report shows: why there is none, or the report itself. */
export type PreviewState = 'pick' | 'several-clients' | 'no-view' | 'loading' | 'error' | 'ready';

/** The send waiting for its confirmation: the lines it goes out for, and how many clients they cover. */
export interface PendingSend {
  lines: ReportGenerateData[];
  studentIds: number[];
  clientCount: number;
}

/**
 * The combined Reports page (reports.combined-page): one filter bar (report type, one or many clients, dates with
 * presets, format), and two actions on it. Download (exactly one client) is View's download, background PDFs included
 * (ClientReportDownload); Send to clients is Generate's batch (client-report-send), for people who may generate
 * reports. Under them, the reports already sent for the selection (api/reports/ViewGeneratedReports), with a link to
 * the History tab for the rest. Filters are remembered and kept in the URL, as on the other Reports screens.
 */
@Component({
  selector: 'reports-combined',
  templateUrl: './reports-combined.component.html',
  styleUrls: ['./reports-combined.component.css'],
  standalone: false
})
export class ReportsCombinedComponent implements OnInit, OnDestroy {
  /** True for people who may generate reports (the ReportsGenerate feature, as the Generate tab needs). */
  @Input() canSend = false;
  /** "See all in History": the History tab, with these filters. */
  @Output() showHistory = new EventEmitter<FilterState>();

  public readonly screenKey = 'reports.combined';
  public form: UntypedFormGroup;
  public pdfEnabled = false;
  public batchPdfEnabled = false;
  public format: ReportFileFormat = 'xlsx';
  public datePreset: DatePreset | null = null;
  /** Several clients: the client picker takes many, and Send makes one report per client in a single batch. */
  public multiClient = false;

  public brandList: BrandInfo[] = [];
  public readonly sessionStatusList = SESSION_REPORT_STATUSES;
  public readonly previewRowLimit = PREVIEW_ROW_LIMIT;

  /** The on-screen report for one client, redrawn as the filters change. */
  public preview: PreviewState = 'pick';
  public previewCode: string = null;
  public previewRows: any[] = null;
  public previewTotalRows = 0;
  public previewMilpr: MilprReport = null;

  public alreadySent: ReportSentData[] = [];
  public alreadySentLoading = false;
  public loadingLines = false;
  public sending = false;
  public pendingSend: PendingSend = null;
  /** The last Send found no bookings that need the report. */
  public noLinesToSend = false;

  private static readonly filterSpec: FilterSpec = {
    from: 'string', to: 'string', preset: 'string', report: 'string', client: 'number[]', coordinator: 'number',
    tutor: 'number', student: 'number[]', brand: 'number[]'
  };
  private filterUx: ScreenFilterUx;
  private readonly filterLookups: ReportFilterLookupsService;
  private pendingFilterState: FilterState = null;
  private restored = false;
  private readonly downloads: ClientReportDownload;
  private readonly refresh = new Subject<void>();
  private readonly unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    public reportDataService: ReportDataService,
    private reportService: ReportService,
    private authService: AuthenticationService,
    private clientReportService: ClientReportService,
    private errorModalHelper: ErrorModalHelperService,
    private successModalHelper: SuccessModalHelperService,
    @Optional() private featureSwitches?: FeatureSwitchService,
    @Optional() private router: Router = null,
    @Optional() private route: ActivatedRoute = null,
    @Optional() filterLookups?: ReportFilterLookupsService,
    @Optional() private dialog: MatDialog = null
  ) {
    this.downloads = new ClientReportDownload(clientReportService, errorModalHelper, this.unsubscribe);
    this.filterLookups = filterLookups ?? new ReportFilterLookupsService(reportService, reportDataService);
  }

  /** The clients picked, as ids. */
  public get clientIds(): number[] {
    const clients = this.form?.value?.clients;
    return clients == null ? [] : (Array.isArray(clients) ? clients : [clients]);
  }

  /** Download is for one client at a time. */
  public get canDownload(): boolean {
    return !this.multiClient && this.clientIds.length === 1;
  }

  /** Switches the client picker between one and several, keeping what was picked where it fits. */
  public setMultiClient(on: boolean): void {
    const ids = this.clientIds;
    this.multiClient = on;
    this.form.patchValue({ clients: on ? ids : (ids[0] ?? null) });
  }

  /** The selected report runs natively: only those download, or have a PDF version. */
  public get isNativeReportSelected(): boolean {
    return getNativeReportCode(this.form?.value?.reports) != null;
  }

  public get showFormat(): boolean {
    return this.pdfEnabled || this.batchPdfEnabled;
  }

  public get preparingPdf(): boolean {
    return this.downloads.preparing;
  }

  public readonly downloadLabel = 'Download';

  /** When the selection was last sent, or null when it hasn't been. */
  public get lastSent(): Date | null {
    return this.alreadySent.length > 0 ? new Date(this.alreadySent[0].generatedDate) : null;
  }

  /** Resend once every client picked has already had this report for these dates. */
  public get isResend(): boolean {
    const sentTo = new Set(this.alreadySent.map(row => row.clientId));
    return this.clientIds.length > 0 && this.clientIds.every(id => sentTo.has(id));
  }

  public get sendLabel(): string {
    const count = this.clientIds.length;
    if (this.multiClient && count > 1) {
      return (this.isResend ? 'Resend to ' : 'Send to ') + count + ' clients';
    }
    return this.isResend ? 'Resend report' : 'Send report';
  }

  /** The status filter only means anything on the session-grain reports. */
  public get showStatusFilter(): boolean {
    const code = getNativeReportCode(this.form?.value?.reports);
    return code === 'attendance' || code === 'milpr';
  }

  /** Coordinator, tutor, learner, brand and status narrow one client's report, so they show in one-client mode. */
  public get showMoreOptions(): boolean {
    return !this.multiClient;
  }

  /** Send needs a report type with a native version and at least one client. */
  public get sendReady(): boolean {
    return this.canSend && this.form?.valid && this.isNativeReportSelected && this.clientIds.length > 0 && !this.sending && !this.loadingLines;
  }

  ngOnInit(): void {
    this.filterUx = new ScreenFilterUx(this.screenKey, ReportsCombinedComponent.filterSpec, this.router, this.route);
    const end = new Date();
    this.form = this.formBuilder.group({
      startDate: [new Date(end.getTime() - (30 * 24 * 60 * 60 * 1000)), [Validators.required]],
      endDate: [end, [Validators.required]],
      reports: [null, [Validators.required]],
      clients: [[]],
      coordinators: [null],
      tutors: [null],
      students: [null],
      brands: [null],
      statuses: [[...ALL_SESSION_REPORT_STATUS_IDS]]
    });

    this.clientReportService.getBrands().pipe(takeUntil(this.unsubscribe))
      .subscribe((data: BrandInfo[]) => this.brandList = data ?? []);
    // One client picked: load its coordinator, tutor and learner lists, and drop choices made for another client.
    // The control's own value: the form's value has not caught up yet when the control emits.
    this.form.controls['clients'].valueChanges.pipe(
      map(value => {
        const ids = value == null ? [] : (Array.isArray(value) ? value : [value]);
        return ids.length === 1 ? ids[0] : null;
      }), distinctUntilChanged(), takeUntil(this.unsubscribe)
    ).subscribe(clientId => {
      this.form.patchValue({ coordinators: null, tutors: null, students: null }, { emitEvent: false });
      if (clientId != null) {
        this.filterLookups.load(clientId);
      }
    });

    this.featureSwitches?.isEnabled(FeatureSwitchKeys.clientReportsPdf).pipe(takeUntil(this.unsubscribe))
      .subscribe(on => this.pdfEnabled = on === true);
    this.featureSwitches?.isEnabled(FeatureSwitchKeys.clientReportsBatchPdf).pipe(takeUntil(this.unsubscribe))
      .subscribe(on => this.batchPdfEnabled = on === true);
    // Only native reports have a PDF version, so a switch to any other report falls back to Excel.
    this.form.controls['reports'].valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(report => {
      if (getNativeReportCode(report) == null) {
        this.format = 'xlsx';
      }
    });

    // The already-sent panel follows the filters once they settle, and reloads after a send.
    this.form.valueChanges.pipe(debounceTime(ALREADY_SENT_DEBOUNCE_MS), takeUntil(this.unsubscribe)).subscribe(() => {
      this.pendingSend = null;
      this.noLinesToSend = false;
      if (this.restored) {
        this.filterUx.save(this.filterState());
      }
      this.refresh.next();
    });
    this.refresh.pipe(switchMap(() => this.loadAlreadySent()), takeUntil(this.unsubscribe))
      .subscribe(rows => this.alreadySent = rows);
    // The on-screen report follows the same settled filters; a newer change cancels a report still loading.
    this.refresh.pipe(switchMap(() => this.loadPreview()), takeUntil(this.unsubscribe)).subscribe();

    this.reportDataService.reportListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => this.applyPendingFilterState());
    this.pendingFilterState = this.filterUx.initialState();
    this.restored = this.pendingFilterState == null;
    this.applyPendingFilterState();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public onDatePreset(selection: DatePresetSelection): void {
    this.datePreset = selection.preset;
    this.form.patchValue({ startDate: parseIsoDate(selection.from), endDate: parseIsoDate(selection.to) });
  }

  /** Clear all: back to the defaults, forgotten, and out of the URL. */
  public clearAll(): void {
    const end = new Date();
    this.form.reset({
      startDate: new Date(end.getTime() - (30 * 24 * 60 * 60 * 1000)), endDate: end, reports: null, clients: null,
      statuses: [...ALL_SESSION_REPORT_STATUS_IDS]
    });
    this.datePreset = null;
    this.multiClient = false;
    this.pendingSend = null;
    this.noLinesToSend = false;
    this.filterUx.clear();
  }

  /** The report for the one client picked, in the chosen format; a large PDF is prepared in the background. */
  public download(): void {
    const code = getNativeReportCode(this.form.value.reports);
    if (!this.canDownload || code == null || this.form.invalid) { return; }
    const reportFilter = this.reportFilter();
    if (this.pdfEnabled && this.format === 'pdf') {
      this.downloads.pdf(code, reportFilter);
    } else {
      this.downloads.excel(code, reportFilter);
    }
  }

  /** The api/ClientReport filter for the one client picked, More Options included. */
  public reportFilter(): ClientReportFilter {
    const value = this.form.value;
    const students = value.students == null ? null : (Array.isArray(value.students) ? value.students : [value.students]);
    return buildClientReportFilter({ ...value, clients: this.clientIds[0] }, students);
  }

  /** Send to clients, step one: find the bookings the report would go out for, and ask before sending. */
  public prepareSend(): void {
    if (!this.sendReady) { return; }
    const requester = {
      userId: this.authService.currentUserValue?.id,
      email: this.authService.userSessionData?.currentUser?.email,
      clientUsers: this.reportDataService.clientUser
    };
    let studentIds: number[] = [];
    const lists = this.clientIds.map(clientId => {
      const built = buildSendListRequest({ ...this.form.value, clients: clientId }, clientId, [], requester);
      studentIds = built.studentIds;
      return this.reportService.generateReportListToSend(built.params).pipe(
        map((lines: ReportGenerateData[]) => linesForLearners(lines ?? [], built.studentIds)));
    });
    this.loadingLines = true;
    forkJoin(lists).subscribe({
      next: perClient => {
        this.loadingLines = false;
        this.pendingSend = { lines: requiredLines([].concat(...perClient)), studentIds, clientCount: this.clientIds.length };
        this.noLinesToSend = this.pendingSend.lines.length === 0;
        if (!this.noLinesToSend) {
          this.askBeforeSending();
        }
      },
      error: error => {
        this.loadingLines = false;
        this.errorModalHelper.show('An error has occurred, please try again', error);
      }
    });
  }

  /** Send to clients, step two: the Generate batch for the lines found. */
  public confirmSend(): void {
    const pending = this.pendingSend;
    if (!pending || pending.lines.length === 0 || this.sending) { return; }
    const formValue = this.form.value;
    const code = getNativeReportCode(formValue.reports);
    if (code == null) { return; }
    const request = buildNativeBatchRequest({ ...formValue, clients: null }, code, pending.studentIds, pending.lines,
      this.batchPdfEnabled && this.format === 'pdf');
    request.filter.clientIds = this.clientIds;
    this.sending = true;
    this.clientReportService.runBatch(request).subscribe({
      next: () => {
        this.sending = false;
        this.pendingSend = null;
        this.noLinesToSend = false;
        this.successModalHelper.show('Successfully generated reports');
        this.refresh.next();
      },
      error: error => {
        this.sending = false;
        this.errorModalHelper.show('An error has occurred, please try again', error);
      }
    });
  }

  public cancelSend(): void {
    this.pendingSend = null;
    this.noLinesToSend = false;
  }

  /** The are-you-sure: what goes, to whom, and whether it has gone before. Only Send in the dialog sends. */
  private askBeforeSending(): void {
    const value = this.form.value;
    const pdf = this.batchPdfEnabled && this.format === 'pdf' && this.isNativeReportSelected;
    const summary = buildSendSummary(this.pendingSend.lines, value.reports?.name, value.startDate, value.endDate, pdf,
      this.lastSent, this.isResend);
    this.dialog.open(SendReportConfirmComponent, { data: summary, autoFocus: '[data-test=send-confirm-cancel]', restoreFocus: true })
      .afterClosed().subscribe(confirmed => confirmed === true ? this.confirmSend() : this.cancelSend());
  }

  /** "See all in History": the History tab with this report type, the client when there is one, and the dates. */
  public seeAllInHistory(): void {
    const state = this.filterState();
    this.showHistory.emit({
      report: state.report, preset: state.preset, from: state.from, to: state.to,
      client: this.clientIds.length === 1 ? this.clientIds[0] : null
    });
  }

  /** The already-sent requests for the selection: one per client (the list filters by one client at a time). */
  public alreadySentRequests(): ReportSentDataRequest[] {
    const value = this.form.value;
    if (this.form.controls['reports'].invalid || !value.startDate || !value.endDate) { return []; }
    const clients: (number | null)[] = this.clientIds.length > 0 ? this.clientIds : [null];
    return clients.map(clientId => {
      const request = new ReportSentDataRequest();
      request.startDate = toRequestDate(value.startDate);
      request.endDate = toRequestDate(value.endDate);
      request.reportType = value.reports.name;
      // As History: the Client Reports role alone decides, so a referral-only link sees the whole client.
      if (clientId != null) {
        request.clientId = clientId;
      }
      request.pageNumber = 1;
      request.pageSize = ALREADY_SENT_PAGE_SIZE;
      request.sortField = 'generatedDate';
      request.sortDirection = 'desc';
      return request;
    });
  }

  /**
   * Draws the report for the client picked. A report type with no native version has no on-screen view. A failure says so on the page rather than in a popup, because this runs
   * on every filter change.
   */
  private loadPreview(): Observable<unknown> {
    const code = getNativeReportCode(this.form.value.reports);
    this.previewRows = null;
    this.previewMilpr = null;
    this.previewCode = null;
    if (this.form.invalid || this.clientIds.length === 0) {
      this.preview = 'pick';
      return of(null);
    }
    if (this.multiClient) {
      this.preview = 'several-clients';
      return of(null);
    }
    if (code == null) {
      this.preview = 'no-view';
      return of(null);
    }
    this.preview = 'loading';
    const filter = this.reportFilter();
    const run: Observable<any> = code === 'milpr' ? this.clientReportService.runMilpr(filter)
      : code === 'attendance' ? this.clientReportService.runAttendance(filter)
      : code === 'cohortprogress' ? this.clientReportService.runCohortProgress(filter)
      : this.clientReportService.runProgressOverview(filter);
    return run.pipe(
      map(data => {
        this.previewCode = code;
        if (code === 'milpr') {
          this.previewMilpr = data;
        } else {
          const rows: any[] = data ?? [];
          this.previewTotalRows = rows.length;
          this.previewRows = rows.slice(0, PREVIEW_ROW_LIMIT);
        }
        this.preview = 'ready';
      }),
      catchError(() => {
        this.preview = 'error';
        return of(null);
      }));
  }

  private loadAlreadySent(): Observable<ReportSentData[]> {
    const requests = this.alreadySentRequests();
    if (requests.length === 0) { return of([]); }
    this.alreadySentLoading = true;
    return forkJoin(requests.map(r => this.reportService.getGeneratedReports(r).pipe(
      map((page: any) => (page?.items ?? []) as ReportSentData[]), catchError(() => of([] as ReportSentData[]))))).pipe(
      map(pages => {
        this.alreadySentLoading = false;
        return [].concat(...pages)
          .sort((a: ReportSentData, b: ReportSentData) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime())
          .slice(0, ALREADY_SENT_PAGE_SIZE);
      }));
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const value = this.form.value;
    return {
      preset: this.datePreset,
      from: this.datePreset || !value.startDate ? null : toIsoDate(new Date(value.startDate)),
      to: this.datePreset || !value.endDate ? null : toIsoDate(new Date(value.endDate)),
      report: value.reports?.name ?? null,
      client: this.clientIds,
      coordinator: value.coordinators,
      tutor: value.tutors,
      student: value.students == null ? [] : (Array.isArray(value.students) ? value.students : [value.students]),
      brand: value.brands ?? []
    };
  }

  /** Restores remembered or linked filters once the report types have arrived. */
  private applyPendingFilterState(): void {
    const state = this.pendingFilterState;
    if (!state || !this.form || (this.reportDataService.reports ?? []).length === 0) {
      return;
    }
    this.pendingFilterState = null;
    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    const from = parseIsoDate(range ? range.from : state.from as string);
    const to = parseIsoDate(range ? range.to : state.to as string);
    // A link with several clients opens in Several clients mode.
    const clients = (state.client as number[]) ?? [];
    this.multiClient = clients.length > 1;
    this.form.patchValue({
      startDate: from ?? this.form.value.startDate,
      endDate: to ?? this.form.value.endDate,
      reports: this.reportDataService.reports.find(r => r.name === state.report) ?? null,
      clients: this.multiClient ? clients : (clients[0] ?? null)
    });
    // After the client, whose change clears these and loads their lists.
    const students = (state.student as number[]) ?? [];
    this.form.patchValue({
      coordinators: (state.coordinator as number) ?? null,
      tutors: (state.tutor as number) ?? null,
      students: students.length > 0 ? students : null,
      brands: ((state.brand as number[]) ?? []).length > 0 ? state.brand : null
    });
    this.restored = true;
  }
}
